import React, { createContext, useContext, useEffect, useState } from 'react'
import { ThemeMode, ThemeConfig } from '@/types'

interface ThemeContextType {
  theme: ThemeConfig
  setTheme: (theme: Partial<ThemeConfig>) => void
  toggleMode: () => void
  isDark: boolean
}

const defaultTheme: ThemeConfig = {
  mode: 'system',
  primaryColor: 'blue',
  accentColor: 'purple',
  borderRadius: 'md',
  density: 'comfortable',
}

const ThemeContext = createContext<ThemeContextType | undefined>(undefined)

export const useTheme = () => {
  const context = useContext(ThemeContext)
  if (context === undefined) {
    throw new Error('useTheme must be used within a ThemeProvider')
  }
  return context
}

interface ThemeProviderProps {
  children: React.ReactNode
}

export const ThemeProvider: React.FC<ThemeProviderProps> = ({ children }) => {
  const [theme, setThemeState] = useState<ThemeConfig>(() => {
    const saved = localStorage.getItem('chefmax-theme')
    return saved ? { ...defaultTheme, ...JSON.parse(saved) } : defaultTheme
  })

  const [isDark, setIsDark] = useState(false)

  useEffect(() => {
    const updateTheme = () => {
      let shouldBeDark = false

      if (theme.mode === 'dark') {
        shouldBeDark = true
      } else if (theme.mode === 'system') {
        shouldBeDark = window.matchMedia('(prefers-color-scheme: dark)').matches
      }

      setIsDark(shouldBeDark)
      
      // Update document classes
      const root = document.documentElement
      root.classList.toggle('dark', shouldBeDark)
      
      // Update CSS custom properties for dynamic theming
      root.style.setProperty('--primary-color', theme.primaryColor)
      root.style.setProperty('--accent-color', theme.accentColor)
      root.style.setProperty('--border-radius', theme.borderRadius)
      root.style.setProperty('--density', theme.density)
    }

    updateTheme()

    // Listen for system theme changes
    const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)')
    const handleChange = () => {
      if (theme.mode === 'system') {
        updateTheme()
      }
    }

    mediaQuery.addEventListener('change', handleChange)
    return () => mediaQuery.removeEventListener('change', handleChange)
  }, [theme])

  const setTheme = (newTheme: Partial<ThemeConfig>) => {
    const updatedTheme = { ...theme, ...newTheme }
    setThemeState(updatedTheme)
    localStorage.setItem('chefmax-theme', JSON.stringify(updatedTheme))
  }

  const toggleMode = () => {
    const modes: ThemeMode[] = ['light', 'dark', 'system']
    const currentIndex = modes.indexOf(theme.mode)
    const nextMode = modes[(currentIndex + 1) % modes.length]
    setTheme({ mode: nextMode })
  }

  const value: ThemeContextType = {
    theme,
    setTheme,
    toggleMode,
    isDark,
  }

  return (
    <ThemeContext.Provider value={value}>
      {children}
    </ThemeContext.Provider>
  )
}
